/**
 * ONE HOUSEHOLD: who they are, who to ring, which home, and what each home has agreed to.
 *
 * ON A PHONE it is one column of cards, read one-handed, in the order somebody standing in a
 * driveway needs them: the people to ring first, then the home, then what was agreed, then the
 * codes, and the history last because it is the widest read in the system and most visits to this
 * page do not need it.
 *
 * ON A DESK the same cards become a record with the history beside it. The left column holds the
 * sections a person edits, the right column holds everything that has already happened, and the
 * two are the reason to sit down rather than carry the phone.
 *
 * THE SPLIT WAITS FOR REAL WIDTH, and xl is not a preference. The history column is a fixed 400px,
 * and at md the 248px rail plus the page padding plus that 400px leave the record about fifty
 * pixels, which is not a record at all; even at lg the history would be wider than the household it
 * is the history of. So the two columns appear at xl and nowhere below it, and every narrower width
 * keeps the phone's single column with the history as a disclosure under it. The inner grids are
 * CONTAINER queries rather than viewport ones: this column is 720px wide at 1440 and about 560px at
 * xl, so a viewport rule would put three columns of facts into a third of the room.
 *
 * Priority 6 of the business brief is every client's history in one place. It is the panel on the
 * right, open on a desk and a details on a phone, so a phone makes no request for it until asked.
 *
 * Two things are given more room than their size suggests. Every person has a tap-to-call button
 * rather than a number to copy, because this is read one-handed in a car. And the do-not list is
 * rendered as its own panel on every home, present or absent, because an absent panel reads as an
 * empty list and an empty list reads as permission.
 *
 * WHAT SHE AGREED TO BE TOLD ON LIVES ON EACH PERSON'S CARD, and it is the second thing on this
 * page that can be written from it. The consent register is authoritative in ops and it is
 * append-only: a withdrawal is a second row rather than an edit, so both controls here record and
 * neither one changes anything that is already written. It belongs beside the person rather than
 * on a screen of its own, because the moment somebody is certainly holding the answer is the
 * moment she is on the phone and her card is open. Until this existed the only place a consent
 * could be recorded was inside the move flow, at the point of being refused by it.
 *
 * THE SOURCE PILL WAS THE FIRST EDIT BUTTON ON THIS PAGE. The server has had the routes all along
 * (PATCH /api/clients/:id and the two POSTs under it), but `web/src/api.ts` exposed none of them,
 * and api.ts is the one place this interface talks to the server; a button that opens nothing is
 * worse than an absent one, so each section was drawn without it. The source is the first to earn
 * one, because it is the answer priority 7 is counted from and the one fact on this page that is
 * regularly wrong: every call opens an enquiry filed as a phone call, and a channel that was a guess
 * at the enquiry is still a guess on the household it converted into. It opens into the same block
 * the enquiry panel and the new-client form draw, and the patch is audited like every other write,
 * so the record keeps what it said before.
 *
 * AND EACH PERSON'S CARD EARNED TWO (ops #76 item 5), once the server had the doors: "Change her
 * details", which sends only what changed and keeps one primary a household (a person made primary
 * demotes the one she replaces; the primary herself is never made anything else), and "Remove from
 * the household", which asks first, is never offered for the primary or the last person, and ends her
 * door onto home with her. A person she asks about from home is decided on the ask, through the same
 * doors.
 *
 * AND THE PAGE SHE ASKED FOR AT HOME TO SAVE A WAY TO PAY (ops #76 item 5), on the Paying card: open at
 * Stripe, saved and waiting on Stripe's small deposits, or ended without saving, so the office knows
 * before it tells her nothing is saved.
 *
 * WHAT CHARGES THE HOUSEHOLD AUTOMATICALLY IS FIRST, when anything does (ops #46, step 3b): the method
 * she saved by ticking Stripe's box, since when and who agreed, with a Stop that asks why before it
 * writes. First because it is the one card on this page about her money leaving without a person
 * pressing anything each time, and the office has to see it before it rings her about an invoice.
 *
 * AND EACH PERSON'S DOOR ONTO HOME (ops #19, step 5), on her card under what she agreed to be told on:
 * invited, on home, or removed, with "Invite to home" where this deployment shares a secret with home
 * and she has an email address, and on the household "Send it all again" once somebody is on home.
 * `components/HomeMembership.tsx` says what each does; the office's routes decide.
 *
 * AND WHAT THEY ASKED FOR THROUGH HOME (ops #19, step 7), "Asks from home", while anything waits on the
 * office: each ask in her words, with Accept and Decline, the same block the follow-up it raised opens
 * (`components/HomeRequest.tsx`). Absent when nothing waits, so a household not on home shows nothing.
 *
 * AND THE TEXTS WITH THE HOUSEHOLD (ops #26, part 2), first among what has already happened: every
 * text her people sent the business line and every text ops composed to them, in the order they
 * happened, each text out with where it stands and why it did not go (`TextThread.tsx`). Beside the
 * record on a desk, above the history; under the record on a phone. Read as the page opens, because
 * it is one cheap read and not step-up gated as the history is. From it the office writes one of her
 * people with a number a text, or answers a text of theirs (ops #32, `Composer.tsx`).
 *
 * AND THE EMAILS TO THE HOUSEHOLD (ops #76 O6), under the texts in a panel of their own: every email ops
 * composed to her people, newest first, with where each stands (`components/HouseholdEmails.tsx`). The
 * thread stays texts only. From it the office writes one of them an email of its own (ops #84 phase 5).
 *
 * AND WHAT EACH PERSON IS SENT (ops #76 phase 2, phase 4 of `docs/plan-preferences.md`): "What we send
 * them" on each person's card, their choices per category and home with the office's change on their
 * word, and "Hold messages" on the household, the office's holds with Lift
 * (`components/WhatWeSend.tsx`). Read once for the household as the page opens; a read that failed
 * says so with a retry and offers no change (gotcha 81), and a Worker older than the door draws nothing.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AGREEMENT_STATE_LABELS,
  CADENCE_LABELS,
  CHANNELS,
  CLIENT_LANGUAGES,
  CONTACT_ROLES,
  OFFICE_REVOKE_REASONS,
  PREFERRED_CHANNELS,
  PRICE_SOURCE_LABELS,
  SERVICE_LINE_LABELS,
  humanise,
  mayLiftMarks,
  type CaptureMethod,
  type ClientLanguage,
  type ContactRole,
  type OfficeRevokeReason,
  type PreferredChannel,
  type RateLine,
} from '@fk/shared';
import {
  ApiError,
  editContact,
  forgetHousehold,
  getCatalog,
  getClient,
  getForgetHold,
  getHouseholdChoices,
  listAgreements,
  listCleaners,
  listHomeRequests,
  listMemberships,
  patchClient,
  recordConsent,
  removeContact,
  stopAutopay,
  needsStepUp,
  type AgreementList,
  type ForgetHold,
  type AutopayView,
  type CleanerRow,
  type ClientRecord,
  type ClientRow,
  type ContactEdit,
  type ContactRow,
  type HomeRequestView,
  type HouseholdChoices,
  type Me,
  type MembershipRow,
  type SetupPageView,
  type SiteRow,
  type StoppedAutopay,
} from '../api.ts';
import { Card, Detail, Pill, ScreenTitle } from '../components/AppShell.tsx';
import { AtAGlance } from '../components/AtAGlance.tsx';
import { CallLink, MailLink } from '../components/CallLink.tsx';
import { CallThrough } from '../components/CallThrough.tsx';
import { DoNotContactPanel } from '../components/DoNotContact.tsx';
import { StopHoldCard } from '../components/StopHold.tsx';
import { DoNotPanel } from '../components/DoNotPanel.tsx';
import { Button, SelectField, TextAreaField, TextField } from '../components/Field.tsx';
import { HomeCodes } from '../components/HomeCodes.tsx';
import { HomeMembership, SendItAllAgain, doorOf } from '../components/HomeMembership.tsx';
import { HomeRequestBody } from '../components/HomeRequest.tsx';
import { HouseholdEmails } from '../components/HouseholdEmails.tsx';
import { SidePanel } from '../components/ListView.tsx';
import { Busy, ErrorNotice, SavedNotice, StepUpNotice } from '../components/Notice.tsx';
import { SourceFields } from '../components/SourceFields.tsx';
import { clientStatusTone } from '../components/StatusPills.tsx';
import { TimelinePanel } from '../components/TimelinePanel.tsx';
import { HoldMessages, WhatWeSend } from '../components/WhatWeSend.tsx';
import {
  CONSENT_CHOICES,
  OFFICE_CAPTURE_METHODS,
  TEXT_CONSENT_NOTE,
  capturedAtFor,
  isAheadOfToday,
  messagingLine,
  purposesOf,
  type ConsentChoice,
} from '../consent.ts';
import {
  E164,
  STATE,
  dayOf,
  formatDay,
  formatInstant,
  formatLocalWindow,
  formatPhone,
  money,
  orNull,
  toE164,
  weekdayName,
} from '../format.ts';
import { HOUSEHOLD_FORGET_WARNING, householdForgottenWords, phoneSystemIn } from '../discard.ts';
import {
  CAPTURE_METHOD_LABELS,
  CHANNEL_LABELS,
  CLIENT_LANGUAGE_LABELS,
  REVOKE_REASON_LABELS,
} from '../labels.ts';
import { sourceAnswerOf, sourcePatch, type SourceAnswer } from '../source.ts';
import { Arrangements, cleanerName } from './Arrangements.tsx';
import { TextThread } from './TextThread.tsx';

function addressOf(site: SiteRow): string {
  const lines = [site.addressLine1];
  if (site.addressLine2) lines.push(site.addressLine2);
  lines.push(`${site.town}, ${STATE} ${site.postalCode}`);
  return lines.join('\n');
}

function sizeOf(site: SiteRow): string | null {
  const parts: string[] = [];
  if (site.bedrooms !== null) parts.push(`${site.bedrooms} bed`);
  if (site.bathrooms !== null) parts.push(`${site.bathrooms} bath`);
  if (site.squareFeet !== null) parts.push(`${site.squareFeet} sq ft`);
  return parts.length > 0 ? parts.join(', ') : null;
}

/** The number in the header: the primary person if she has one, else the first person who has. */
function whoToRing(contacts: readonly ContactRow[]): ContactRow | null {
  return (
    contacts.find((contact) => contact.role === 'primary' && contact.phoneE164) ??
    contacts.find((contact) => contact.phoneE164) ??
    null
  );
}

export function ClientDetail({ me }: { me: Me }) {
  const { id = '' } = useParams();
  const [record, setRecord] = useState<ClientRecord | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [cleaners, setCleaners] = useState<readonly CleanerRow[] | null>(null);
  const [lines, setLines] = useState<readonly RateLine[] | null>(null);
  const [correcting, setCorrecting] = useState(false);
  // The language they read, opened from its pill (ops #98).
  const [choosingLanguage, setChoosingLanguage] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [doors, setDoors] = useState<readonly MembershipRow[]>([]);
  const [asks, setAsks] = useState<readonly HomeRequestView[]>([]);
  // Moved by a text sent from the Emails card's composer, so the thread beside it reads again.
  const [textsRead, setTextsRead] = useState(0);
  // What each person is sent and the office's holds (ops #76 phase 2): unread, read, absent on a Worker
  // older than the door, or failed, which says so rather than drawing nobody's choices (gotcha 81).
  const [choices, setChoices] = useState<HouseholdChoices | 'unread' | 'absent' | 'failed'>(
    'unread',
  );

  useEffect(() => {
    let live = true;
    getHouseholdChoices(id)
      .then((answer) => {
        if (live) setChoices(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setChoices(thrown instanceof ApiError && thrown.status === 404 ? 'absent' : 'failed');
      });
    return () => {
      live = false;
    };
  }, [id, attempt]);

  useEffect(() => {
    let live = true;
    // What waits on the office from home (ops #19, step 7). The page reads without it: an ask the read
    // did not bring is still on the follow-ups list, which is where it was raised.
    listHomeRequests(id)
      .then((answer) => {
        if (live) setAsks(answer.requests);
      })
      .catch(() => {
        if (live) setAsks([]);
      });
    return () => {
      live = false;
    };
  }, [id, attempt]);

  useEffect(() => {
    let live = true;
    // Who may see this household at home (ops #19). The cards read without it: a door the read did
    // not bring is a pill not drawn, never a person wrongly shown as invited.
    listMemberships(id)
      .then((answer) => {
        if (live) setDoors(answer.memberships);
      })
      .catch(() => {
        if (live) setDoors([]);
      });
    return () => {
      live = false;
    };
  }, [id, attempt]);

  useEffect(() => {
    let live = true;
    setRecord(null);
    setError(null);
    getClient(id)
      .then((answer) => {
        if (live) setRecord(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'That household did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [id, attempt]);

  useEffect(() => {
    let live = true;
    listCleaners()
      .then((answer) => {
        if (live) setCleaners(answer.cleaners);
      })
      .catch(() => {
        // Names on a schedule are decoration here; an id is shown when the crew did not load.
      });
    getCatalog()
      .then((answer) => {
        if (live) setLines(answer.lines);
      })
      .catch(() => {
        // Without the card nothing can be priced, and the arrangements panel says so in words.
      });
    return () => {
      live = false;
    };
  }, []);

  if (error) {
    return (
      <div>
        <BackLink />
        <ErrorNotice
          title="That household did not load"
          message={error.message}
          onRetry={() => setAttempt((n) => n + 1)}
        />
      </div>
    );
  }

  if (!record) {
    return (
      <div>
        <BackLink />
        <Busy what="this household" />
      </div>
    );
  }

  const { client, contacts, sites } = record;
  const since = formatDay(dayOf(client.createdAt));
  const ring = whoToRing(contacts);
  // The page she asked for at home to save a way to pay, while it is still the news: once it saved a
  // method that method's own line says so (ops #76 item 5).
  const setupPage = record.setupPage ?? null;
  const setupNews = setupPage !== null && setupPage.standing !== 'saved' ? setupPage : null;
  const primaryName = contacts.find((contact) => contact.role === 'primary')?.displayName ?? null;
  const done = (sentence: string) => {
    setSaid(sentence);
    setAttempt((n) => n + 1);
  };
  /** One person's choices, where the household's read came back and holds them. */
  const choicesOf = (contactId: string) =>
    typeof choices === 'object'
      ? (choices.people.find((one) => one.personId === contactId) ?? null)
      : null;

  return (
    <div>
      <BackLink />

      <ScreenTitle title={client.displayName} />

      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-6">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Pill tone={clientStatusTone(client.status)}>{humanise(client.status)}</Pill>
          {/* The one editable fact in this header, and the button is the pill itself: a separate
              pencil beside it would be a second target for one thing. It is gold, not coral: a
              source nobody has corrected is a fact rather than a gap. */}
          <button
            type="button"
            onClick={() => setCorrecting((open) => !open)}
            aria-expanded={correcting}
            className="inline-flex min-h-11 desk:min-h-9 items-center"
          >
            <Pill tone="gold">Found through {CHANNEL_LABELS[client.sourceChannel]}</Pill>
          </button>
          {/* THE LANGUAGE THEY READ (ops #98), the pill itself the button as the source's is. Drawn
              only where the read carries it, so a Worker older than the column offers no change. */}
          {client.language ? (
            <button
              type="button"
              onClick={() => setChoosingLanguage((open) => !open)}
              aria-expanded={choosingLanguage}
              className="inline-flex min-h-11 desk:min-h-9 items-center"
            >
              <Pill>Reads: {CLIENT_LANGUAGE_LABELS[client.language]}</Pill>
            </button>
          ) : null}
          {since ? <Pill>A client since {since}</Pill> : null}
          {client.endedOn ? <Pill>Ended {formatDay(client.endedOn)}</Pill> : null}
        </div>
        {ring ? (
          <div className="shrink-0">
            <CallLink
              phoneE164={ring.phoneE164}
              marked={ring.messaging?.doNotContact?.call ?? null}
            />
          </div>
        ) : null}
      </div>

      {said ? (
        <div className="mb-4">
          <SavedNotice>{said}</SavedNotice>
        </div>
      ) : null}

      {choosingLanguage && client.language ? (
        <Card className="mb-4">
          <ReadsIn
            key={client.id}
            clientId={client.id}
            current={client.language}
            onSaved={(sentence) => {
              setChoosingLanguage(false);
              done(sentence);
            }}
            onCancel={() => setChoosingLanguage(false)}
          />
        </Card>
      ) : null}

      {correcting ? (
        <Card className="mb-4">
          <FoundThrough
            key={client.id}
            client={client}
            referrerName={record.referrerName}
            onSaved={(sentence) => {
              setCorrecting(false);
              setSaid(sentence);
              setAttempt((n) => n + 1);
            }}
            onCancel={() => setCorrecting(false)}
          />
        </Card>
      ) : (
        <>
          {client.sourceDetail ? (
            <p className="mb-4 text-sm text-navy/75">{client.sourceDetail}</p>
          ) : null}
          {client.referredByClientId ? (
            <p className="mb-4 text-sm text-navy/75">
              Introduced by{' '}
              <Link to={`/clients/${client.referredByClientId}`} className="font-medium underline">
                {record.referrerName ?? 'a household in the book'}
              </Link>
              .
            </p>
          ) : null}
        </>
      )}

      {/* The household at a glance (ops #84 phase 2): facts in fixed sentences, a band across the top
          on a desk and one card above the people on a phone. */}
      <AtAGlance key={client.id} clientId={client.id} />

      {/* The history is second in the source as well as on the right, so a phone reads the record
          first and meets the disclosure last, and the desk reads the same order left to right. */}
      <div className="xl:grid xl:grid-cols-[minmax(0,1fr)_400px] xl:items-start xl:gap-5">
        <div className="@container flex min-w-0 flex-col gap-4">
          {record.autopay || record.autopayStopped || setupNews ? (
            <Section title="Paying">
              {record.autopay ? (
                <Autopay
                  key={record.autopay.authorizationId}
                  clientId={client.id}
                  autopay={record.autopay}
                  savedAtHome={
                    setupPage?.standing === 'saved' &&
                    setupPage.authorizationId === record.autopay.authorizationId
                  }
                  onDone={done}
                />
              ) : record.autopayStopped ? (
                <AutopayStopped stopped={record.autopayStopped} />
              ) : null}
              {setupNews ? (
                <SetupPageLine page={setupNews} stands={record.autopay?.displayLabel ?? null} />
              ) : null}
            </Section>
          ) : null}

          {asks.length > 0 ? (
            <Section title="Asks from home" note="Each one waits on the office.">
              {/* The glance's asks line links here (ops #84). */}
              <ul id="asks-from-home" className="grid gap-4">
                {asks.map((ask) => (
                  <li key={ask.id}>
                    <HomeRequestBody
                      view={ask}
                      me={me}
                      onDecided={(sentence) => {
                        setSaid(sentence);
                        setAttempt((n) => n + 1);
                      }}
                    />
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}

          <Section title="People">
            {contacts.length === 0 ? (
              <p className="text-sm text-coral">
                Nobody is on file for this household. There is nobody to tell when a visit moves.
              </p>
            ) : (
              <ul className="grid gap-4 @xl:grid-cols-2">
                {contacts.map((contact) => (
                  <li key={contact.id}>
                    <Person
                      contact={contact}
                      clientId={client.id}
                      today={me.today}
                      canLift={mayLiftMarks(me.role)}
                      placeCalls={me.placeCalls === true}
                      primaryName={primaryName}
                      onlyPerson={contacts.length < 2}
                      onHome={(doorOf(doors, contact.id)?.state ?? 'revoked') !== 'revoked'}
                      onDone={done}
                    >
                      {choicesOf(contact.id) ? (
                        <WhatWeSend
                          clientId={client.id}
                          contact={contact}
                          person={choicesOf(contact.id)!}
                          sites={sites}
                          today={me.today}
                          onDone={done}
                        />
                      ) : choices === 'failed' ? (
                        <ChoicesUnread onRetry={() => setAttempt((n) => n + 1)} />
                      ) : null}
                      <HomeMembership
                        key={doorOf(doors, contact.id)?.id ?? 'none'}
                        clientId={client.id}
                        contact={contact}
                        sites={sites}
                        door={doorOf(doors, contact.id)}
                        homeSeam={me.homeSeam === true}
                        householdEnded={client.status === 'ended'}
                        onDone={(sentence) => {
                          setSaid(sentence);
                          setAttempt((n) => n + 1);
                        }}
                      />
                    </Person>
                  </li>
                ))}
              </ul>
            )}
            {typeof choices === 'object' ? (
              <HoldMessages
                clientId={client.id}
                contacts={contacts}
                sites={sites}
                holds={choices.holds}
                today={me.today}
                onDone={done}
              />
            ) : null}
            <SendItAllAgain
              clientId={client.id}
              memberships={doors}
              homeSeam={me.homeSeam === true}
              onDone={setSaid}
            />
          </Section>

          {sites.length === 0 ? (
            <Card>
              <p className="text-sm text-coral">
                No home on file, so there is nothing to schedule.
              </p>
            </Card>
          ) : (
            sites.map((site) => (
              <div key={site.id} className="flex flex-col gap-4">
                <Section
                  title={site.label ?? 'The home'}
                  pill={<Pill>{humanise(site.county)} County</Pill>}
                >
                  {/* The home's own page (ops #84 phase 2): its agreements, codes, cleans and timeline. */}
                  <Link
                    to={`/homes/${site.id}`}
                    className="inline-flex min-h-11 desk:min-h-9 items-center self-start text-sm font-medium underline"
                  >
                    Open this home
                  </Link>
                  <dl className="grid gap-4 @md:grid-cols-2 @2xl:grid-cols-3">
                    <Detail label="Address" value={addressOf(site)} />
                    <Detail label="Size" value={sizeOf(site)} />
                    <Detail
                      label="Last professional clean"
                      value={
                        site.lastProfessionalCleanOn
                          ? `${formatDay(site.lastProfessionalCleanOn)}${
                              site.lastProfessionalCleanBasis
                                ? `, ${humanise(site.lastProfessionalCleanBasis).toLowerCase()}`
                                : ''
                            }`
                          : null
                      }
                    />
                    <Detail label="Pets" value={site.pets} />
                    <Detail label="Getting to the door" value={site.entryNotes} />
                    <Detail label="Gated community" value={site.communityName} />
                    <Detail
                      label="Vendor registration"
                      value={
                        site.communityName || site.vendorRegistrationState !== 'not_required'
                          ? [
                              humanise(site.vendorRegistrationState),
                              site.vendorRegistrationExpiresOn
                                ? `until ${formatDay(site.vendorRegistrationExpiresOn)}`
                                : null,
                            ]
                              .filter(Boolean)
                              .join(', ')
                          : null
                      }
                    />
                    <Detail label="Notes" value={site.notes} />
                  </dl>

                  <div className="grid items-start gap-4 @xl:grid-cols-2">
                    <DoNotPanel text={site.doNotList} />
                    <StandingArrangement site={site} cleaners={cleaners} />
                  </div>
                </Section>

                <div className="grid items-start gap-4 @xl:grid-cols-2">
                  <Section
                    title="Agreements"
                    note="Cadence sets the price, so changing it ends one and opens the next."
                  >
                    {/* The panel carries its own rule and its own top margin, so the section pulls
                        it back up to the gap every other section header uses. */}
                    <div className="-mt-4">
                      <Arrangements
                        site={site}
                        today={me.today}
                        cleaners={cleaners}
                        lines={lines}
                      />
                    </div>
                  </Section>

                  <Section title="Codes" note="Each showing asks for a fresh sign-in.">
                    <HomeCodes siteId={site.id} returnPath={`/clients/${client.id}`} />
                  </Section>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Keyed by the household, so walking from one client to the next never shows the texts or
            the history of the one before it while the next one loads. */}
        <div className="min-w-0">
          <div className="mt-4 xl:mt-0 xl:mb-4">
            <SidePanel title="Texts">
              <TextThread
                key={`${client.id}:${textsRead}`}
                of={{ kind: 'client', id: client.id }}
                people={contacts}
              />
            </SidePanel>
          </div>
          {/* Her emails beside her texts, never in them (ops #76 O6). */}
          <div className="mt-4 xl:mt-0 xl:mb-4">
            <HouseholdEmails
              key={client.id}
              clientId={client.id}
              people={contacts}
              onTextSent={() => setTextsRead((n) => n + 1)}
            />
          </div>
          <TimelinePanel key={client.id} clientId={client.id} />
        </div>
      </div>

      {/* FORGET THIS HOUSEHOLD (ops #82): drawn for an owner alone, and the server refuses anybody
          else whatever is drawn (binding rule 6). The last section of the page, under the record and
          its history, at a phone and at a desk alike. */}
      {me.role === 'owner' ? (
        <div className="mt-4">
          <ForgetThisHousehold key={client.id} clientId={client.id} name={client.displayName} />
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The parts of the record.
 * ------------------------------------------------------------------ */

/**
 * FORGET THIS HOUSEHOLD (ops #82, `docs/plan-household-forget.md` section 8): a past client who
 * asked to be deleted. A quiet control at the bottom of the record, never coral, since it is nobody's
 * gap, opening into a step and never acting on the first press, as the discard does. Drawn for an
 * owner alone; the server refuses the manager and the office `403` whatever is drawn, and is the
 * control (binding rule 6).
 *
 * WHILE A HOLD STANDS THE PAGE SAYS WHICH, in the server's own words, in place of the button: an
 * agreement or a visit still on the calendar, an invoice still open ("Settle first, then erase"), an
 * issue, a saved method, or a receipt still being made. A read that failed draws nothing at all, never
 * a button a hold would refuse.
 *
 * THE STEP says what goes and what stays, and asks for the household's name as the page shows it; the
 * server compares it trimmed and in any case. A step-up refusal sends the owner to sign in again and
 * back here. Forgotten, the page goes to the client list, which no longer lists them, with the done
 * sentence.
 */
function ForgetThisHousehold({ clientId, name }: { clientId: string; name: string }) {
  const navigate = useNavigate();
  const [hold, setHold] = useState<ForgetHold | null | 'unread'>('unread');
  const [asked, setAsked] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<ApiError | null>(null);

  useEffect(() => {
    let live = true;
    getForgetHold(clientId)
      .then((answer) => {
        if (live) setHold(answer.hold);
      })
      .catch(() => {
        // A read that failed is not a household with nothing standing (gotcha 81): nothing is drawn.
        if (live) setHold('unread');
      });
    return () => {
      live = false;
    };
  }, [clientId]);

  if (hold === 'unread') return null;

  async function confirm() {
    setBusy(true);
    setProblem(null);
    try {
      const answer = await forgetHousehold(clientId, typed);
      navigate('/clients', {
        state: {
          said: householdForgottenWords(phoneSystemIn(answer)),
          sharing: answer.sharing ?? null,
        },
      });
    } catch (thrown) {
      setProblem(
        thrown instanceof ApiError ? thrown : new ApiError(0, 'error', 'That was not recorded.'),
      );
      setBusy(false);
    }
  }

  return (
    <Section title="Forget this household">
      {hold !== null ? (
        <p className="text-sm text-navy/75">{hold.message}</p>
      ) : !asked ? (
        <div>
          <Button tone="quiet" onClick={() => setAsked(true)}>
            Forget this household
          </Button>
          <p className="mt-1 text-xs text-navy/65">
            For a past client who asked to be deleted. It opens a step first.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-navy/20 p-3">
          {problem ? (
            <div className="mb-3">
              {needsStepUp(problem) ? (
                <StepUpNotice error={problem} />
              ) : (
                <ErrorNotice title="Not recorded" message={problem.message} />
              )}
            </div>
          ) : null}
          <p className="mb-4 rounded-lg border border-navy/20 bg-navy/5 p-3 text-sm">
            {HOUSEHOLD_FORGET_WARNING}
          </p>
          <TextField
            id={`forget-${clientId}-name`}
            label="The household's name"
            value={typed}
            onChange={setTyped}
            autoComplete="off"
            hint={`As the page shows it: ${name}`}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={confirm} disabled={busy || typed.trim() === ''}>
              {busy ? 'Recording' : 'Forget this household'}
            </Button>
            <Button
              tone="quiet"
              onClick={() => {
                setAsked(false);
                setTyped('');
                setProblem(null);
              }}
              disabled={busy}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Section>
  );
}

/**
 * WHERE THIS HOUSEHOLD CAME FROM, corrected.
 *
 * The whole vocabulary is offered, `website_form` included: that word is refused on an enquiry
 * nobody signed, because a form lead carries a lead id that proves it, and a household typed in by
 * hand has no such proof to offer and no such claim to make. Filing her as `other` instead is what
 * would corrupt the count. On a referral the introducer is chosen out of the book, and moving the
 * channel off a referral clears the credit rather than leaving a payment owed under a word that
 * cannot explain it.
 */
function FoundThrough({
  client,
  referrerName,
  onSaved,
  onCancel,
}: {
  client: ClientRow;
  referrerName: string | null;
  onSaved: (sentence: string) => void;
  onCancel: () => void;
}) {
  const current = sourceAnswerOf(client);
  const [answer, setAnswer] = useState<SourceAnswer>(current);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /** On a refusal the server's sentence is shown and nothing typed is touched. */
  async function save() {
    const patch = sourcePatch(answer, current);
    if (Object.keys(patch).length === 0) {
      setProblem('Nothing here has changed yet.');
      return;
    }
    setSaving(true);
    setProblem(null);
    try {
      await patchClient(client.id, patch);
      onSaved('Saved. The history keeps what it said before this.');
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not recorded.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <h2 className="mb-3 font-serif text-lg">Where this household came from</h2>

      {problem ? (
        <div className="mb-4">
          <ErrorNotice title="Not recorded" message={problem} />
        </div>
      ) : null}

      <SourceFields
        idPrefix={`client-source-${client.id}`}
        channels={CHANNELS}
        value={answer}
        onChange={setAnswer}
        channelLabel="How were they found"
        channelHint="Corrected, never guessed. What it said before stays in the history."
        detailHint="Which Nextdoor thread, which realtor's office, which letter batch."
        referrer={
          client.referredByClientId && referrerName
            ? { id: client.referredByClientId, name: referrerName }
            : null
        }
      />

      <div className="flex flex-col gap-3 md:flex-row">
        <Button onClick={save} disabled={saving} full>
          {saving ? 'Recording' : 'Save where they came from'}
        </Button>
        <Button tone="quiet" onClick={onCancel} disabled={saving}>
          Leave it as it is
        </Button>
      </div>
    </div>
  );
}

/**
 * THE LANGUAGE THEY READ (ops #98, `docs/plan-client-language.md` section 3): English or Spanish,
 * set on the client's word and never guessed, sent alone so the patch changes nothing else, and
 * audited by the server with what it said before. Home is told which by the household's push.
 */
function ReadsIn({
  clientId,
  current,
  onSaved,
  onCancel,
}: {
  clientId: string;
  current: ClientLanguage;
  onSaved: (sentence: string) => void;
  onCancel: () => void;
}) {
  const [language, setLanguage] = useState<ClientLanguage>(current);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (language === current) {
      setProblem('Nothing here has changed yet.');
      return;
    }
    setSaving(true);
    setProblem(null);
    try {
      await patchClient(clientId, { language });
      onSaved(
        `Saved. They read ${CLIENT_LANGUAGE_LABELS[language]}; the history keeps what it said before this.`,
      );
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not recorded.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <h2 className="mb-3 font-serif text-lg">The language they read</h2>

      {problem ? (
        <div className="mb-4">
          <ErrorNotice title="Not recorded" message={problem} />
        </div>
      ) : null}

      <SelectField
        id={`client-language-${clientId}`}
        label="They read"
        value={language}
        onChange={setLanguage}
        options={CLIENT_LANGUAGES}
        toLabel={(one) => CLIENT_LANGUAGE_LABELS[one]}
        hint="Set on the client's word, never guessed. Their copy at home is told which."
      />

      <div className="flex flex-col gap-3 md:flex-row">
        <Button onClick={save} disabled={saving} full>
          {saving ? 'Recording' : 'Save the language'}
        </Button>
        <Button tone="quiet" onClick={onCancel} disabled={saving}>
          Leave it as it is
        </Button>
      </div>
    </div>
  );
}

/**
 * WHAT WE SEND THEM DID NOT LOAD: said, with a retry, and no change offered on a read nobody has, since
 * a form built on nothing would send a choice decided on nothing (gotcha 81).
 */
function ChoicesUnread({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="rounded-lg border border-navy/15 p-3" role="alert">
      <p className="text-sm">What we send them did not load, so it cannot be changed here yet.</p>
      <div className="mt-2">
        <Button tone="quiet" onClick={onRetry}>
          Try again
        </Button>
      </div>
    </div>
  );
}

/**
 * One section of the record. The heading, whatever small word belongs beside it, and the body.
 * There is no Edit button on a section: an edit belongs to the thing it changes, a person's to her
 * card, for the reason at the top of this file.
 */
function Section({
  title,
  pill,
  note,
  children,
}: {
  title: string;
  pill?: ReactNode;
  note?: string;
  children: ReactNode;
}) {
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <h2 className="font-serif text-lg leading-7">{title}</h2>
        {pill}
        {note ? <span className="text-xs leading-4 text-navy/65">{note}</span> : null}
      </div>
      {children}
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * What charges the household automatically.
 * ------------------------------------------------------------------ */

/**
 * THE SAVED METHOD THAT STANDS (ops #46, step 3b): which, since when and who agreed, and the office's
 * Stop. She saved it by ticking Stripe's own box on a page ops made, and it is charged only after she
 * tells us a clean went well, or the office has checked with her. A stop is recorded with who, when,
 * why and through which door, so it asks why first and writes nothing until the second press; the
 * method comes off her Customer at Stripe after the answer. The forget is never a reason here, because
 * only the forget may say one happened.
 */
function Autopay({
  clientId,
  autopay,
  savedAtHome,
  onDone,
}: {
  clientId: string;
  autopay: AutopayView;
  /** Saved on a page she asked for at home with nothing owed (ops #76 item 5). */
  savedAtHome: boolean;
  onDone: (sentence: string) => void;
}) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState<OfficeRevokeReason>('client_request');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const agreed = autopay.contactName ? `, agreed by ${autopay.contactName}` : '';

  async function stop() {
    setBusy(true);
    setProblem(null);
    try {
      const answer = await stopAutopay(clientId, reason);
      onDone(
        answer.stopped
          ? `Stopped. Nothing is charged to ${autopay.displayLabel} from now on, and it comes off at Stripe.`
          : 'It had already been stopped. Nothing more was written.',
      );
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not written down.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p className="text-sm">
        Charges automatically: {autopay.displayLabel}, since {formatDay(dayOf(autopay.since))}
        {agreed}.
      </p>
      {savedAtHome ? (
        <p className="mt-1 text-sm">Saved on a page they asked for at home, with nothing owed.</p>
      ) : null}
      <p className="mt-1 text-xs text-navy/65">
        Only after they tell us a clean went well, or the office has checked with them, and never
        anything but the invoice.
      </p>

      {problem ? (
        <div className="mt-3">
          <ErrorNotice title="Not stopped" message={problem} />
        </div>
      ) : null}

      {asking ? (
        <div className="mt-3 rounded-lg border border-coral/50 border-l-4 border-l-coral bg-parchment p-3">
          <p className="text-sm">
            Stop charging {autopay.displayLabel} automatically? Their invoices are then paid the
            other ways their page offers, and it comes off at Stripe. A new one is saved only when
            they tick to save one again, on a Stripe page or on the bank screen of their
            invoice&apos;s own page.
          </p>
          <div className="mt-3">
            <SelectField
              id="autopay-stop-reason"
              label="Why"
              value={reason}
              onChange={setReason}
              options={OFFICE_REVOKE_REASONS}
              toLabel={(value) => REVOKE_REASON_LABELS[value]}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => void stop()} disabled={busy}>
              {busy ? 'Writing it down' : 'Yes, stop charging it'}
            </Button>
            <Button
              tone="quiet"
              onClick={() => {
                setAsking(false);
                setProblem(null);
              }}
              disabled={busy}
            >
              Never mind
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-2">
          <Button tone="quiet" onClick={() => setAsking(true)}>
            Stop charging it
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * THE METHOD LAST STOPPED, while none stands (the staging walk of 2026-09-23): after the Stop the
 * household said nothing about the method at all, so the office could not tell one that never saved a
 * method from one that stopped it. Which method, when, who at the office pressed Stop or Stripe when
 * Stripe did, and why, until a new method is saved.
 */
function AutopayStopped({ stopped }: { stopped: StoppedAutopay }) {
  const who = stopped.stoppedByName ?? (stopped.stoppedVia === 'stripe' ? 'Stripe' : null);
  const why = stopped.reason ? `: ${REVOKE_REASON_LABELS[stopped.reason]}.` : '.';
  return (
    <div>
      <p className="text-sm">{stopped.displayLabel} no longer charges automatically.</p>
      <p className="mt-1 text-sm">
        Stopped on {formatDay(dayOf(stopped.stoppedAt))}
        {who ? ` by ${who}` : ''}
        {why}
      </p>
      <p className="mt-1 text-xs text-navy/65">
        A new one is saved only when they tick the box on a Stripe page, or on the bank screen of
        their invoice&apos;s own page.
      </p>
    </div>
  );
}

/**
 * THE PAGE SHE ASKED FOR AT HOME TO SAVE A WAY TO PAY (ops #76 item 5), while it is still the news:
 * being made, open at Stripe, saved on Stripe's page and not recorded yet, or ended without saving.
 * The server reads where it stands from the row and the clock; this only says it.
 *
 * SAVED BESIDE A METHOD THAT STANDS IS NOT WAITING ON STRIPE (the review's C11-5). A method is never
 * recorded over the one that stands (`recordSetupOptIn`, the office told once by its task), so such a
 * page stays completed with nothing recorded for good, and the line says that rather than that Stripe
 * may still be checking small deposits, which would contradict the task. `stands` is the label of the
 * method that charges the household, or null where none does.
 */
function SetupPageLine({ page, stands }: { page: SetupPageView; stands: string | null }) {
  const what = page.kind === 'bank' ? 'a bank account' : 'a card';
  const who = page.contactName ?? 'Somebody on the household';
  const on = formatDay(dayOf(page.claimedAt));
  const saved = `${who} saved ${what} on a page they asked for at home, on ${formatDay(dayOf(page.completedAt))}.`;
  const sentence =
    page.standing === 'making'
      ? `${who} asked at home on ${on} for a page to save ${what}. Stripe is making it now.`
      : page.standing === 'open'
        ? `${who} asked at home on ${on} for a page to save ${what}. It is open at Stripe until ${formatInstant(page.expiresAt)}, and nothing is charged on it.`
        : page.standing === 'checking'
          ? stands !== null
            ? `${saved} It is not recorded, and is not while ${stands} charges the household: ops never records a second method over the one that stands. Ask them which one they want charged.`
            : `${saved} It is not recorded yet: Stripe may still be checking it by small deposits, and nothing charges until it is recorded here.`
          : `The last page ${who} asked for at home to save ${what}, on ${on}, ended without saving anything.`;
  return (
    <div className="mt-2">
      <p className="text-sm">{sentence}</p>
      {page.standing !== 'ended' ? (
        <p className="mt-1 text-xs text-navy/65">
          A method saved there is charged only after they tell us a clean went well, or the office
          has checked with them, and never for an invoice already sent.
        </p>
      ) : null}
    </div>
  );
}

/**
 * One person: who she is, how she wants to hear from us, and the button that rings her; her own
 * record changed or her taken off the household (ops #76 item 5); and under it, her door onto home,
 * which the page hands in as `children` (ops #19).
 */
function Person({
  contact,
  clientId,
  today,
  canLift,
  placeCalls,
  primaryName,
  onlyPerson,
  onHome,
  onDone,
  children,
}: {
  contact: ContactRow;
  clientId: string;
  today: string;
  /** An owner or a manager, who may lift a do-not-contact mark. The server decides either way. */
  canLift: boolean;
  /** Call through the business line places a call from this deployment (`me.placeCalls`, ops #84). */
  placeCalls: boolean;
  /** The household's primary contact by name, whom a person made primary replaces. */
  primaryName: string | null;
  /** She is the only person on the household, who is never removed. */
  onlyPerson: boolean;
  /** She has a door onto home, invited or on it, which an address changed or a removal reaches. */
  onHome: boolean;
  onDone: (sentence: string) => void;
  children?: ReactNode;
}) {
  const [step, setStep] = useState<'shut' | 'edit' | 'remove'>('shut');
  const finished = (sentence: string) => {
    setStep('shut');
    onDone(sentence);
  };
  return (
    <div id={`person-${contact.id}`} className="flex flex-col gap-2">
      <div className="flex flex-col gap-2 @sm:flex-row @sm:items-center @sm:gap-3">
        <div className="flex min-w-0 grow flex-col gap-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-serif text-base leading-tight">{contact.displayName}</h3>
            <Pill>{humanise(contact.role)}</Pill>
          </div>
          {contact.preferredChannel ? (
            <p className="text-[13px] leading-[18px] text-navy/75">
              Wants to hear from us by {humanise(contact.preferredChannel).toLowerCase()}.
            </p>
          ) : (
            <p className="text-[13px] leading-[18px] text-navy/60">
              Nobody has asked how they want to hear from us. That is not the same as a text being
              fine.
            </p>
          )}
        </div>
        {contact.phoneE164 ? (
          <div className="shrink-0">
            <CallLink
              phoneE164={contact.phoneE164}
              marked={contact.messaging?.doNotContact?.call ?? null}
            />
          </div>
        ) : null}
      </div>
      {/* Under the row rather than beside the name, twice over. An address is long, and squeezed
          into half a cell it breaks mid-word; and what CallLink draws when there is no number is a
          sentence, not a button, which pinned beside the name at its max-content width would hang
          out over the edge of the card. */}
      {contact.phoneE164 ? null : <CallLink phoneE164={null} />}
      {/* Call through the business line (ops #84 phase 6), under her number: never for a person
          with no number or marked not to be called, whom CallLink already says so of. */}
      {contact.phoneE164 && !contact.messaging?.doNotContact?.call ? (
        <CallThrough
          clientId={clientId}
          contactId={contact.id}
          name={contact.displayName}
          on={placeCalls}
        />
      ) : null}
      <MailLink email={contact.email} />
      {step === 'edit' ? (
        <ContactEditor
          contact={contact}
          clientId={clientId}
          primaryName={primaryName}
          onHome={onHome}
          onDone={finished}
          onCancel={() => setStep('shut')}
        />
      ) : step === 'remove' ? (
        <RemovePerson
          contact={contact}
          clientId={clientId}
          onHome={onHome}
          onDone={finished}
          onCancel={() => setStep('shut')}
        />
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button tone="quiet" onClick={() => setStep('edit')}>
            Change details
          </Button>
          {/* Never the primary, whom another is made first, and never the last person, since a
              household needs somebody to tell when a visit moves. The server refuses both too. */}
          {contact.role !== 'primary' && !onlyPerson ? (
            <Button tone="quiet" onClick={() => setStep('remove')}>
              Remove from the household
            </Button>
          ) : null}
        </div>
      )}
      <Messages
        contact={contact}
        clientId={clientId}
        today={today}
        canLift={canLift}
        onDone={onDone}
      />
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Her own record, changed, or her taken off the household (ops #76 item 5).
 * ------------------------------------------------------------------ */

/**
 * HER NAME, HER ROLE, HER NUMBER, HER ADDRESS AND HOW SHE WANTS TO HEAR FROM US, changed: only what
 * changed is sent, and a number or an address emptied is cleared. ONE PRIMARY A HOUSEHOLD: a person
 * made primary replaces the primary there is, who becomes a second household contact in the same
 * step, and the primary herself is offered no other role, so a household never loses its one. Her
 * address is what she signs in to home with and her number the register's key, so the server pushes
 * her door onto home and refuses a number changed under the box she ticked for texts; its sentence is
 * shown as it comes back, and nothing typed is touched.
 */
function ContactEditor({
  contact,
  clientId,
  primaryName,
  onHome,
  onDone,
  onCancel,
}: {
  contact: ContactRow;
  clientId: string;
  primaryName: string | null;
  onHome: boolean;
  onDone: (sentence: string) => void;
  onCancel: () => void;
}) {
  const field = (name: string) => `contact-${contact.id}-${name}`;
  const isPrimary = contact.role === 'primary';
  const [name, setName] = useState(contact.displayName);
  const [role, setRole] = useState<ContactRole>(contact.role);
  const [phone, setPhone] = useState(formatPhone(contact.phoneE164) ?? '');
  const [email, setEmail] = useState(contact.email ?? '');
  const [channel, setChannel] = useState<PreferredChannel | ''>(contact.preferredChannel ?? '');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    const patch: { -readonly [K in keyof ContactEdit]: ContactEdit[K] } = {};
    if (name.trim() === '') {
      setProblem('They need a name.');
      return;
    }
    if (name.trim() !== contact.displayName) patch.displayName = name.trim();
    if (role !== contact.role) patch.role = role;
    const number = phone.trim() === '' ? null : toE164(phone);
    if (number !== null && !E164.test(number)) {
      setProblem('That number does not look right. Type its ten digits, or leave it empty.');
      return;
    }
    if (number !== contact.phoneE164) patch.phoneE164 = number;
    const address = email.trim() === '' ? null : email.trim();
    if (address !== contact.email) patch.email = address;
    const way = channel === '' ? null : channel;
    if (way !== contact.preferredChannel) patch.preferredChannel = way;
    if (Object.keys(patch).length === 0) {
      setProblem('Nothing here has changed yet.');
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      await editContact(clientId, contact.id, patch);
      onDone(
        [
          'Saved. The history keeps what it said before this.',
          patch.role === 'primary' && primaryName
            ? `${primaryName} is a second household contact now.`
            : null,
          patch.email !== undefined && onHome
            ? `Home is told, and ${name.trim()} signs in there with the new address from now on.`
            : null,
        ]
          .filter(Boolean)
          .join(' '),
      );
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-navy/20 p-3">
      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not saved" message={problem} />
        </div>
      ) : null}
      <TextField id={field('name')} label="Name" value={name} onChange={setName} required />
      {isPrimary ? (
        <p className="mb-3 text-xs text-navy/65">
          They are the household&apos;s primary contact, and a household always has one. Make
          another person primary on their card: they become a second household contact then.
        </p>
      ) : (
        <SelectField
          id={field('role')}
          label="Who they are to the household"
          value={role}
          onChange={setRole}
          options={CONTACT_ROLES}
          toLabel={(value) => humanise(value)}
          hint={
            role === 'primary' && primaryName
              ? `${primaryName} is the primary contact now, and becomes a second household contact when you save.`
              : undefined
          }
        />
      )}
      <TextField
        id={field('phone')}
        label="Number"
        type="tel"
        inputMode="tel"
        value={phone}
        onChange={setPhone}
        hint="Empty clears it. Their texts stand on the box they ticked for the number they have, so a new number holds no agreement to texts."
      />
      <TextField
        id={field('email')}
        label="Email"
        type="email"
        inputMode="email"
        value={email}
        onChange={setEmail}
        hint={
          onHome
            ? 'Empty clears it. They sign in to home with this address, so home is told when it changes.'
            : 'Empty clears it.'
        }
      />
      <SelectField
        id={field('channel')}
        label="How they want to hear from us"
        value={channel}
        onChange={setChannel}
        options={PREFERRED_CHANNELS}
        toLabel={(value) => humanise(value)}
        placeholder="Nobody has asked"
      />
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => void save()} disabled={busy}>
          {busy ? 'Saving' : 'Save details'}
        </Button>
        <Button tone="quiet" onClick={onCancel} disabled={busy}>
          Leave them as they are
        </Button>
      </div>
    </div>
  );
}

/**
 * HER TAKEN OFF THE HOUSEHOLD: asked first, and nothing written until the second press. Her record is
 * soft deleted and stays in the history, her door onto home ends with her and every ask of hers still
 * waiting is cancelled, in the one step the server takes.
 */
function RemovePerson({
  contact,
  clientId,
  onHome,
  onDone,
  onCancel,
}: {
  contact: ContactRow;
  clientId: string;
  onHome: boolean;
  onDone: (sentence: string) => void;
  onCancel: () => void;
}) {
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function remove() {
    setBusy(true);
    setProblem(null);
    try {
      const answer = await removeContact(clientId, contact.id);
      onDone(
        `Removed. ${contact.displayName} is off the household, and the history keeps them.${
          answer.doorEnded ? ' Their door onto home ended with it.' : ''
        }`,
      );
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not written down.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-coral/50 border-l-4 border-l-coral bg-parchment p-3">
      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not removed" message={problem} />
        </div>
      ) : null}
      <p className="text-sm">
        Take {contact.displayName} off this household? Their record stays in the history, and
        nothing more goes to them about it.
        {onHome
          ? ' Their door onto home ends with it, and any ask of theirs still waiting is cancelled.'
          : ''}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button onClick={() => void remove()} disabled={busy}>
          {busy ? 'Writing it down' : 'Yes, take them off'}
        </Button>
        <Button tone="quiet" onClick={onCancel} disabled={busy}>
          Never mind
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * What she agreed to be told on.
 * ------------------------------------------------------------------ */

/**
 * WHETHER SHE MAY BE MESSAGED, AND HOW THAT WAS RECORDED.
 *
 * Ops is authoritative for consent and the register is append-only, so both controls here write a
 * row and neither one edits one: a withdrawal is a second row with `kind` withdrawn, and the
 * position in force is the last row for the person and the purpose. Nothing is deleted, so what
 * she agreed to in September is still readable after she withdraws it in October, which is the
 * only reason to keep a register rather than a flag.
 *
 * A REFUSAL IS THE SAFE ANSWER AND AN UNKNOWN ONE IS NOT A REFUSAL. When the household read does
 * not carry the register's answer, the line says what the rule is rather than claiming a position:
 * telling somebody a client refused, when nobody has looked, sends her to ring a client who agreed
 * months ago.
 *
 * RECORD CONSENT RECORDS EMAILS ONLY, SINCE OPS #51: a yes to texts is the box on the website quote
 * form, which reaches the register by itself, and the note says so where the choice is made.
 * Withdraw still withdraws both, because a refusal is always honoured.
 *
 * AND DO NOT CONTACT UNDER IT (ops #44 step two): a line for each mark that holds on her, her number
 * or her address, with who, when and why; Mark for anybody in the office, Lift for an owner or a
 * manager, and the history (`components/DoNotContact.tsx`).
 */
function Messages({
  contact,
  clientId,
  today,
  canLift,
  onDone,
}: {
  contact: ContactRow;
  clientId: string;
  today: string;
  canLift: boolean;
  onDone: (sentence: string) => void;
}) {
  const line = messagingLine(contact.messaging);
  const [step, setStep] = useState<'shut' | 'record' | 'withdraw'>('shut');
  const [choice, setChoice] = useState<ConsentChoice>('email');
  const [how, setHow] = useState<CaptureMethod>('verbal_on_call');
  const [day, setDay] = useState(today);
  const [words, setWords] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const field = (name: string) => `consent-${contact.id}-${name}`;

  async function run(work: () => Promise<unknown>, sentence: string) {
    setBusy(true);
    setProblem(null);
    try {
      await work();
      setStep('shut');
      setWords('');
      onDone(sentence);
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not written down.');
    } finally {
      setBusy(false);
    }
  }

  function record() {
    if (isAheadOfToday(day, today)) {
      setProblem(
        'Consent is recorded when it is given, so it cannot carry a day that has not come.',
      );
      return;
    }
    const purposes = purposesOf(choice);
    void run(
      () =>
        recordConsent(clientId, contact.id, {
          purposes,
          kind: 'granted',
          captureMethod: how,
          capturedAt: capturedAtFor(day, today),
          evidenceText: orNull(words),
        }),
      `Written down. ${contact.displayName} may be told about their cleans that way.`,
    );
  }

  function withdraw() {
    if (isAheadOfToday(day, today)) {
      setProblem(
        'A withdrawal is recorded when it is heard, so it cannot carry a day that has not come.',
      );
      return;
    }
    void run(
      () =>
        recordConsent(clientId, contact.id, {
          purposes: ['operational_sms', 'operational_email'],
          kind: 'withdrawn',
          captureMethod: how,
          capturedAt: capturedAtFor(day, today),
          evidenceText: orNull(words),
        }),
      `Written down. Nothing goes to ${contact.displayName} until they agree again.`,
    );
  }

  return (
    <div className="rounded-lg border border-navy/15 p-3">
      <p className="text-xs tracking-wide text-navy/55 uppercase">Messages</p>
      <p className="mt-1 text-sm">{line.sentence}</p>
      {line.evidence ? <p className="mt-1 text-xs text-navy/65">{line.evidence}</p> : null}

      {problem ? (
        <div className="mt-3">
          <ErrorNotice title="Not written down" message={problem} />
        </div>
      ) : null}

      {step === 'shut' ? (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button tone="quiet" onClick={() => setStep('record')}>
            Record consent
          </Button>
          <Button tone="quiet" onClick={() => setStep('withdraw')}>
            Withdraw
          </Button>
        </div>
      ) : null}

      {step === 'record' ? (
        <div className="mt-3 rounded-lg border border-navy/20 p-3">
          <fieldset>
            <legend className="text-sm font-medium">What they agreed to</legend>
            <div className="mt-2 flex flex-col gap-2">
              {CONSENT_CHOICES.map((option) => (
                <label
                  key={option.id}
                  htmlFor={field(option.id)}
                  className={`flex min-h-11 desk:min-h-9 cursor-pointer items-center gap-3 rounded-lg border p-3 ${
                    choice === option.id ? 'border-navy bg-gold/10' : 'border-navy/25'
                  }`}
                >
                  <input
                    id={field(option.id)}
                    type="radio"
                    name={field('purpose')}
                    checked={choice === option.id}
                    onChange={() => setChoice(option.id)}
                    className="h-5 w-5 shrink-0 accent-navy"
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <p id={field('texts-note')} className="mt-2 text-xs text-navy/65">
            {TEXT_CONSENT_NOTE}
          </p>

          <div className="mt-3">
            <HowAndWhen
              field={field}
              how={how}
              onHow={setHow}
              day={day}
              onDay={setDay}
              words={words}
              onWords={setWords}
              wordsHint="What they actually said. For a consent given on a call this is the only evidence there will ever be."
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={record} disabled={busy}>
              {busy ? 'Writing it down' : 'Record what they agreed to'}
            </Button>
            <Button tone="quiet" onClick={() => setStep('shut')} disabled={busy}>
              Never mind
            </Button>
          </div>
        </div>
      ) : null}

      {step === 'withdraw' ? (
        <div className="mt-3 rounded-lg border border-coral/50 border-l-4 border-l-coral bg-parchment p-3">
          <p className="text-sm">
            Record that {contact.displayName} has withdrawn their agreement to messages? Nothing
            goes to them by text or email after this, on any subject, until they agree again. What
            they agreed to before stays on the record exactly as it is.
          </p>

          <div className="mt-3">
            <HowAndWhen
              field={field}
              how={how}
              onHow={setHow}
              day={day}
              onDay={setDay}
              words={words}
              onWords={setWords}
              wordsHint="How they put it, in their words."
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={withdraw} disabled={busy}>
              {busy ? 'Writing it down' : 'Yes, they have withdrawn it'}
            </Button>
            <Button tone="quiet" onClick={() => setStep('shut')} disabled={busy}>
              Never mind
            </Button>
          </div>
        </div>
      ) : null}

      <DoNotContactPanel
        contact={contact}
        canLift={canLift}
        today={today}
        onDone={onDone}
        held={heldOnHerNumber(contact, canLift, onDone)}
      />
    </div>
  );
}

/**
 * THE STOP IN HER OWN WORDS WAITING ON HER NUMBER (ops #33), decided here as on the Texts screen: the
 * same card, with Clear for an owner or a manager, and the household read again after a decision, as
 * after a mark. Nothing while no hold is open, and nothing from a Worker older than the field.
 */
function heldOnHerNumber(
  contact: ContactRow,
  canClear: boolean,
  onDone: (sentence: string) => void,
): ReactNode {
  const hold = contact.messaging?.hold;
  if (!hold || hold.outcome !== null) return null;
  return (
    <StopHoldCard hold={hold} sender={null} body={hold.body} canClear={canClear} onDone={onDone} />
  );
}

/** The evidence half, the same three questions on both steps: how, when, and in whose words. */
function HowAndWhen({
  field,
  how,
  onHow,
  day,
  onDay,
  words,
  onWords,
  wordsHint,
}: {
  field: (name: string) => string;
  how: CaptureMethod;
  onHow: (value: CaptureMethod) => void;
  day: string;
  onDay: (value: string) => void;
  words: string;
  onWords: (value: string) => void;
  wordsHint: string;
}) {
  return (
    <>
      <SelectField
        id={field('how')}
        label="How they gave it"
        value={how}
        onChange={onHow}
        options={OFFICE_CAPTURE_METHODS}
        toLabel={(value) => CAPTURE_METHOD_LABELS[value]}
      />
      <TextField
        id={field('day')}
        label="The day they said it"
        type="date"
        value={day}
        onChange={onDay}
        hint="Today unless this is being written up afterwards. It is when they said it that counts, never when it was typed."
      />
      <TextAreaField
        id={field('words')}
        label="Their words"
        value={words}
        onChange={onWords}
        rows={2}
        hint={wordsHint}
      />
    </>
  );
}

/**
 * What this home has standing right now: how often, when, whose slot it is, and what the card
 * prices it at. It reads the same endpoint the agreements card below reads, because the card is a
 * component this screen does not own and its props do not carry the answer back up.
 */
function StandingArrangement({
  site,
  cleaners,
}: {
  site: SiteRow;
  cleaners: readonly CleanerRow[] | null;
}) {
  const [list, setList] = useState<AgreementList | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setList(null);
    setFailed(null);
    listAgreements(site.id)
      .then((answer) => {
        if (live) setList(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setFailed(
          thrown instanceof ApiError ? thrown.message : 'The standing arrangement did not load.',
        );
      });
    return () => {
      live = false;
    };
  }, [site.id]);

  const standing =
    list?.agreements.find((row) => row.state === 'active') ??
    list?.agreements.find((row) => row.state === 'paused') ??
    null;
  const series = standing
    ? (list?.series.find((row) => row.agreementId === standing.id && !row.endedOn) ?? null)
    : null;

  return (
    <div className="mt-3 flex flex-col gap-2 rounded-lg border border-navy/15 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs leading-4 tracking-wide text-navy/55 uppercase">
          The standing arrangement
        </span>
        {standing && standing.state !== 'active' ? (
          <Pill tone="gold">{AGREEMENT_STATE_LABELS[standing.state]}</Pill>
        ) : null}
      </div>

      {failed ? (
        <p className="text-sm" role="alert">
          {failed}
        </p>
      ) : list === null ? (
        <p className="text-sm text-navy/60" role="status">
          Loading the standing arrangement.
        </p>
      ) : !standing ? (
        <p className="text-sm text-navy/70">
          Nothing standing yet. The agreements card sets the first one up, priced off the published
          card.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2.5">
            <Fact label="Cadence" value={CADENCE_LABELS[standing.cadence]} />
            <Fact
              label="Window"
              value={
                series
                  ? `${weekdayName(series.anchorDate).slice(0, 3)}, ${formatLocalWindow(
                      series.windowStartLocal,
                      series.windowEndLocal,
                    )}`
                  : 'A one-off, no standing slot'
              }
            />
            <Fact label="Cleaner" value={cleanerName(cleaners, series?.cleanerId ?? null)} />
            <Fact label="Price" value={money(standing.priceCents)} />
          </div>
          <p className="text-xs leading-4 text-navy/65">
            {SERVICE_LINE_LABELS[standing.serviceLine]}, since {formatDay(standing.startedOn)}.{' '}
            {PRICE_SOURCE_LABELS[standing.priceSource]}.
          </p>
        </>
      )}
    </div>
  );
}

/** One small labelled figure inside the standing arrangement box. */
function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-px">
      <span className="text-xs leading-4 text-navy/55">{label}</span>
      <span className="text-sm leading-5 font-medium">{value}</span>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/clients"
      className="mb-2 -ml-2 inline-flex min-h-11 desk:min-h-9 items-center gap-1 px-2 text-sm font-medium text-navy/70"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 20 20"
        className="h-4 w-4 shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 5l-5 5 5 5" />
      </svg>
      Back to clients
    </Link>
  );
}
